import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  ViewChild,
  ViewContainerRef,
  ComponentFactoryResolver,
  OnDestroy,
  ChangeDetectorRef,
  Output,
  EventEmitter,
  HostListener, ElementRef
} from '@angular/core';
import { PanelsService } from '../../services/panels.service';
import { Subject } from 'rxjs/Subject';
import { EmitterTypes } from '../../constants/emitter-types.enum';
import { TunnelResult } from '../../classes/tunnel-result';
import { EmitType } from '../../constants/emit-types.enum';
import { ITunnelDataInterface } from '../../interfaces/tunnel-data.interface';
import { LayoutLoaderAbstract } from '../../classes/layout-loader.abstract';
import { PanelLoaderPayload } from '@ispm/layout/classes/panel-loader-payload.abstract';
import {Widget} from "@ispm/widgets/decorator/widget.decorator";
import {IWidgetItem} from "@ispm/widgets/interfaces/widget-item.interface";
import {IdynamicFormItem} from "@ispm/dynamic-form/interfaces/IdynamicFormItem";
import { get,has } from 'lodash';
import {ISlidePanelConfig} from "@ispm/therapies/interfaces/view-configuration.interface";

export enum PanelPosition {
  LEFT = 'left',
  RIGHT = 'right',
  CENTER = 'center'
}

@Component({
  selector: 'gen-panel',
  templateUrl: './panel.component.html',
  styleUrls: ['./panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

@Widget('panelWidget')
export class PanelComponent extends LayoutLoaderAbstract implements OnInit, OnDestroy , IWidgetItem {
  @Input() name: string;
  @Input() title: string;
  @Input() classes: string;
  @Input() position: PanelPosition = PanelPosition.LEFT;
  @Input() panelId: string = '';
  @Output() toggleDisplayStatusChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() modal: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild('loadTarget', { static: false, read: ViewContainerRef}) loadTarget;
  @ViewChild('sidePanel') sidePanel: ElementRef;
  @Input() displayStatus: boolean = false;
  @Input() showCloseSideBarIcon: boolean = false;
  @Input() fullHeight: boolean = false;
  @Output() onToggleSidePanelBar: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() onSetSidePanelBarTitle: EventEmitter<string> = new EventEmitter<string>();
  private completed: boolean = false;
  private tunnel: Subject<TunnelResult> = new Subject<TunnelResult>();
  private cmpRef: any;
  private panelConfig:any;
  private data:any;
  public displayStatusShifted: boolean = false;
  private shiftUsed: boolean = false;

  constructor(_panelService: PanelsService,
              private _changeDetector: ChangeDetectorRef,
              private _componentFactoryResolver: ComponentFactoryResolver) {
    super(_panelService);
  }

  ngOnInit() {
    this.initListener(this.name);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._changeDetector.detach();
    this.dismiss(this.name);
  }

  set widgetConfig(widgetConfig: IdynamicFormItem<any>) {
    this.panelConfig = get(widgetConfig, 'widgetPayload', { });
    if(this.panelConfig) {
      this.setPanelConfig(this.panelConfig);
      this.initListener(this.panelConfig.name);
    }
  }
  set widgetData(widgetData: any) {
    this.data = widgetData;
  }

  setPanelConfig(panelConfig: ISlidePanelConfig) {
    this.name = panelConfig.name ? panelConfig.name : this.name;
    this.title = panelConfig.title ? panelConfig.title : this.title;
    this.classes = panelConfig.classes ? panelConfig.classes : this.classes;
    this.position = panelConfig.position ? panelConfig.position : this.position;
    this.onSetSidePanelBarTitle.emit(this.title);
  }

  onNext(result: TunnelResult) {
    switch (result.responseType) {
      case EmitType.Data:
        this.tunnel.next(result.getData());
        break;
      case EmitType.Dismiss:
        this.toggle(false);
        break;
      case EmitType.Open:
        const data = result.getData() as ITunnelDataInterface;
        this.loadComponent(data.componentToLoad, data.payload);
        this.toggle(true);
        break;

    }
  }

  onError(err) {
    console.error(err);
  }

  public isFunction(functionToCheck) {
    return functionToCheck && { }.toString.call(functionToCheck) === '[object Function]';
  }

  onComplete() {
    this.title = '';
    this.completed = true;
    try {
      this.toggle(false);
    } catch (err) {
      console.warn('toggle is not a function...');
    }
  }

  public closePanel() {
    this.toggle(false);
    this.modal.emit();
  }

  public closeSidePanel() {
    this.onToggleSidePanelBar.emit(false);
  }

  public toggle(displayStatus: boolean) {
    this.displayStatus = displayStatus;
    if(this.shiftUsed) {
      this.displayStatusShifted = displayStatus;
    }
    this.toggleDisplayStatusChanged.emit(displayStatus);
    if (this.completed) {
      return;
    }
    this._changeDetector.markForCheck();
  }

  protected loadComponent(componentToLoad: any, definition: PanelLoaderPayload) {
    const factory = this._componentFactoryResolver.resolveComponentFactory(componentToLoad);
    if (this.cmpRef) {
      this.cmpRef.destroy();
    }
    this.cmpRef = this.loadTarget.createComponent(factory);
    this.cmpRef.instance['tunnel$'] = this.tunnel;
    this.cmpRef.instance['callback'] = definition.approveCallback;
    this.cmpRef.instance['payload'] = definition.payload;
    // this.classes = definition.classes;
    this.tunnel.asObservable()
      .takeUntil(this.destroyer.asObservable())
      .filter(value => value.emitter !== EmitterTypes.parent)
      .subscribe(next => {
        this.proxy({ data: next.getData(), status: EmitType.Dismiss});
      });
    if (definition.hasOwnProperty('title')) {
      this.setTitle(definition.title);
    }
    if (has(definition,'payload.classes')) {
      this.classes = definition.payload.classes;
    }
    if (has(definition,'payload.id')) {
      this.classes = definition.payload.id;
    }
    if (has(definition,'payload.shifted')) {
      this.shiftUsed = definition.payload.shifted;
      this.displayStatusShifted = definition.payload.shifted;
    }
  }

  private setTitle(title: string) {
    this.title = title;
    this.onSetSidePanelBarTitle.emit(this.title);
  }

  private proxy(payload) {
    this._panelService.next(this.name, payload, EmitterTypes.parent);
  }

  ngAfterViewInit() {
    if (this.fullHeight) {
      this.resizePanel();
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    if (this.fullHeight) {
      this.resizePanel();
    }
  }

  private resizePanel() {
    setTimeout(() => {
      this.resizePanelHeight();
    }, 200);
  }

  private resizePanelHeight() {
    const appRootEl = document.getElementsByTagName('app-root')[0] as HTMLElement;
    const ispmNotifEl = appRootEl?.getElementsByTagName('ispm-notification')[0] as HTMLElement;
    const genHomeEl = appRootEl?.getElementsByTagName('gen-home')[0] as HTMLElement;
    const mainHeaderEl = genHomeEl?.getElementsByTagName('header')[0] as HTMLElement;
    const genCurationEditorHeaderEl = appRootEl?.getElementsByTagName('gen-curation-editor-header')[0] as HTMLElement;
    const genCurationEditorActionsEl = appRootEl?.getElementsByTagName('gen-curation-editor-actions')[0] as HTMLElement;
    let panelHeight = this.calculateHeight(appRootEl?.getBoundingClientRect().height, [ispmNotifEl, mainHeaderEl, genCurationEditorHeaderEl, genCurationEditorActionsEl]);
    this.sidePanel.nativeElement.setAttribute('style', 'height: ' + panelHeight + 'px !important;');
  }

  private calculateHeight(height: number, elements: Array<HTMLElement>) {
    elements.forEach((el) => {
      if (el) {
        height = height - el.getBoundingClientRect().height;
      }
    });
    return height;
  }
}
